import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BlueprintItemDataEditorService } from '../blueprint-item-data-editor.service';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ButtonGroup } from 'primeng/buttongroup';
import { DragDropModule } from 'primeng/dragdrop';
import { InputText } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, map, Observable, startWith, Subscription } from 'rxjs';
import { Blueprint } from '../../../../models/blueprint.model';
import {
  getQuantityTypeOptions,
  QuantityType,
  QuoteItemData,
  INHERIT_MATERIAL_ID,
} from '../../../../models/quote-item-data.model';
import { quantityFieldValidator } from '../../../../validators/quantity-field.validator';
import { SelectModule } from 'primeng/select';
import { MaterialSelectComponent } from '../../../shared/material-select/material-select.component';
import { TaxCodeSelectComponent } from '../../../tax-code-select/tax-code-select.component';
import { InputNumber } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { Equipment } from '../../../../models/equipment.model';
import {
  Consumable,
  ConsumableSelection,
} from '../../../../models/consumable.model';
import { ItemConsumablesEditorComponent } from '../../../shared/item-consumables-editor/item-consumables-editor.component';
import { PricingModelService } from '../../../services/pricing-model.service';

interface TreePageData {
  dragging: QuoteItemData | false;
}

@Component({
  selector: 'app-blueprint-item-data-tree',
  standalone: true,
  imports: [
    CommonModule,
    Button,
    Tooltip,
    ButtonGroup,
    DragDropModule,
    InputText,
    FormsModule,
    ReactiveFormsModule,
    MaterialSelectComponent,
    TaxCodeSelectComponent,
    SelectModule,
    InputNumber,
    MultiSelectModule,
    ItemConsumablesEditorComponent,
  ],
  templateUrl: './blueprint-item-data-tree.component.html',
  styleUrl: './blueprint-item-data-tree.component.scss',
})
/**
 * Component for displaying a single item in the quote item data tree and its children in a tree structure.
 *
 * **About ref-* items:**
 * ref-* items are temporary, read-only display items created when viewing blueprint contents.
 * They have persistent_ids starting with "ref-" (e.g., "ref-123-0") and represent items from
 * a blueprint template shown in read-only mode. They allow viewing nested blueprint structures
 * without making them editable. These items are NOT saved to the database - they exist only
 * for display purposes and are created on-demand when a blueprint reference is expanded.
 */
export class BlueprintItemDataTree implements OnInit, OnChanges, OnDestroy {
  @Input() item!: QuoteItemData;
  @Input() allItems: QuoteItemData[] = [];
  @Input() allBlueprints: Blueprint[] = [];
  @Input() depth: number = 0;
  @Input() currencyCode?: string;
  @Input() equipmentOptions: Equipment[] = [];
  @Input() consumableOptions: Consumable[] = [];
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Input() consumablesEnabled = false;
  @Output() addChild = new EventEmitter<string>();
  @Output() removeItem = new EventEmitter<string>();

  blueprintItemDataEditorService: BlueprintItemDataEditorService = inject(
    BlueprintItemDataEditorService,
  );
  pricingModelService: PricingModelService = inject(PricingModelService);

  fb = new FormBuilder();

  expanded: boolean = false;
  referencedBlueprint: Blueprint | null = null;
  referencedBlueprintItems: QuoteItemData[] = [];
  private blueprintLoadAttempted: boolean = false;

  // Form for editing item fields
  form = this.fb.group({
    name: [''],
    quantity: ['', quantityFieldValidator()],
    unit_cost: [''],
    markup_percentage: ['0'],
    quantity_type: [QuantityType.UNIT],
    batch_size: [null as number | null],
    pricing_model_id: [null as number | null],
    material_id: [null as number | null],
    tax_code_id: [null as number | null],
    capex_equipment_ids: [[] as number[]],
  });

  private formSubscription: Subscription | null = null;
  private quantityTypeSubscription: Subscription | null = null;

  quantityTypeOptions = getQuantityTypeOptions(false, 'short');

  pageData$: Observable<TreePageData> = combineLatest([
    this.blueprintItemDataEditorService.dragging$.pipe(
      startWith(false as QuoteItemData | false),
    ),
  ]).pipe(
    map(([dragging]) => {
      return {
        dragging,
      };
    }),
  );

  ngOnInit(): void {
    if (this.item) {
      this.loadValues();
      this.setupFormSubscription();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.item) {
      console.log('ngOnChanges called');
      this.loadValues();
      if (!this.formSubscription) {
        this.setupFormSubscription();
      }
      // Reset blueprint loading state when item changes
      this.blueprintLoadAttempted = false;
      this.referencedBlueprint = null;
      this.referencedBlueprintItems = [];
    }
  }

  private loadValues(): void {
    if (!this.item) return;

    this.form.patchValue(
      {
        name: this.item.name || '',
        quantity: this.item.quantity || '',
        unit_cost: this.item.unit_cost || '',
        markup_percentage: this.item.markup_percentage ?? '0',
        quantity_type: this.item.quantity_type || QuantityType.UNIT,
        batch_size:
          this.item.batch_size != null && this.item.batch_size > 0
            ? this.item.batch_size
            : null,
        pricing_model_id: this.item.pricing_model_id || null,
        material_id: this.item.inherit_material
          ? INHERIT_MATERIAL_ID
          : this.item.material_id || null,
        tax_code_id: this.item.tax_code_id ?? null,
        capex_equipment_ids: this.item.capex_equipment_ids ?? [],
      },
      { emitEvent: false },
    );

    this.form.markAsPristine({ emitEvent: false });

    // Update disabled state
    if (this.isBlueprintReferenceItem) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private setupFormSubscription(): void {
    // Unsubscribe from previous subscription if it exists
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    if (this.quantityTypeSubscription) {
      this.quantityTypeSubscription.unsubscribe();
    }

    this.quantityTypeSubscription =
      this.form.get('quantity_type')?.valueChanges.subscribe(() => {
        this.form.get('quantity')?.updateValueAndValidity({ emitEvent: false });
      }) ?? null;

    // Subscribe to form changes and update the item via service
    // This ensures the item is updated in the service's items array, maintaining consistency
    this.formSubscription = this.form.valueChanges.subscribe((formValue) => {
      if (this.isBlueprintReferenceItem || !this.item) {
        return;
      }

      // Only update if values actually changed to prevent infinite loops
      const name = formValue.name || '';
      const quantity = formValue.quantity || undefined;
      const unit_cost = formValue.unit_cost || undefined;
      const markup_percentage =
        formValue.markup_percentage === null ||
        formValue.markup_percentage === undefined
          ? undefined
          : formValue.markup_percentage;
      const quantity_type =
        formValue.quantity_type === null ||
        formValue.quantity_type === undefined
          ? undefined
          : (formValue.quantity_type as QuantityType);
      const batch_size =
        formValue.batch_size != null && formValue.batch_size > 0
          ? formValue.batch_size
          : null;
      const pricing_model_id =
        formValue.pricing_model_id === null ||
        formValue.pricing_model_id === undefined
          ? undefined
          : formValue.pricing_model_id;
      const selectedMaterialId =
        formValue.material_id === null || formValue.material_id === undefined
          ? null
          : Number(formValue.material_id);
      const inherit_material = selectedMaterialId === INHERIT_MATERIAL_ID;
      const material_id = inherit_material ? null : selectedMaterialId;
      const tax_code_id = formValue.tax_code_id ?? null;
      const capex_equipment_ids = formValue.capex_equipment_ids ?? [];
      const itemBatchSize =
        this.item.batch_size != null && this.item.batch_size > 0
          ? this.item.batch_size
          : null;

      // Check if any value actually changed
      if (
        name === this.item.name &&
        quantity === this.item.quantity &&
        unit_cost === this.item.unit_cost &&
        markup_percentage === this.item.markup_percentage &&
        quantity_type === this.item.quantity_type &&
        batch_size === itemBatchSize &&
        pricing_model_id === this.item.pricing_model_id &&
        material_id === this.item.material_id &&
        inherit_material === (this.item.inherit_material ?? false) &&
        tax_code_id === this.item.tax_code_id &&
        this.areNumberArraysEqual(
          capex_equipment_ids,
          this.item.capex_equipment_ids ?? [],
        )
      ) {
        return; // No changes, skip update
      }

      // Update the item via service to maintain consistency with the items array
      this.blueprintItemDataEditorService.updateItem(this.item, {
        name,
        quantity,
        unit_cost,
        markup_percentage,
        quantity_type,
        batch_size,
        pricing_model_id,
        material_id,
        inherit_material,
        tax_code_id,
        capex_equipment_ids,
      });
    });
  }

  get children(): QuoteItemData[] {
    const serviceItems = this.blueprintItemDataEditorService.items();
    const actualChildren = serviceItems.filter(
      (item) => item.parent_id === this.item.persistent_id,
    );

    // If children exist in service items, return them
    if (actualChildren.length > 0) {
      return this.blueprintItemDataEditorService.sortSiblingsBySiblingOrder(
        actualChildren,
      );
    }

    // For ref-* items: check referencedBlueprintItems (for on-demand loaded items)
    if (
      this.isBlueprintReferenceItem &&
      this.isBlueprintLinkItem &&
      this.expanded
    ) {
      const childrenFromReferenced = this.referencedBlueprintItems.filter(
        (item) => item.parent_id === this.item.persistent_id,
      );
      if (childrenFromReferenced.length > 0) {
        return this.blueprintItemDataEditorService.sortSiblingsBySiblingOrder(
          childrenFromReferenced,
        );
      }
      return [];
    }

    // For regular items with blueprint_id: show read-only blueprint children
    if (
      this.isBlueprintLinkItem &&
      !this.isBlueprintReferenceItem &&
      this.expanded
    ) {
      return this.topLevelReferencedBlueprintChildren;
    }

    return [];
  }

  get referencedBlueprintChildren(): QuoteItemData[] {
    // Return referenced blueprint items for ref-* items when expanded
    return this.expanded && this.isBlueprintLinkItem
      ? this.referencedBlueprintItems
      : [];
  }

  get topLevelReferencedBlueprintChildren(): QuoteItemData[] {
    // Return top-level items from referenced blueprint (parent_id === null)
    // Used for non-ref items to display the referenced blueprint's contents read-only
    if (
      this.isBlueprintLinkItem &&
      !this.isBlueprintReferenceItem &&
      this.expanded &&
      this.referencedBlueprint
    ) {
      return this.referencedBlueprintItems.filter(
        (item) => item.parent_id === null,
      );
    }
    return [];
  }

  onToggleExpanded(): void {
    this.expanded = !this.expanded;
    // Load referenced blueprint when expanding a blueprint item
    if (
      this.expanded &&
      this.isBlueprintLinkItem &&
      !this.blueprintLoadAttempted
    ) {
      this.loadReferencedBlueprint();
    }
  }

  private loadReferencedBlueprint(): void {
    if (this.blueprintLoadAttempted) {
      return;
    }
    this.blueprintLoadAttempted = true;

    const blueprint = this.allBlueprints.find(
      (b) => String(b.id) === String(this.item.blueprint_id),
    );
    if (!blueprint?.items?.length) {
      if (!blueprint) {
        console.warn(
          `Blueprint ${this.item.blueprint_id} not found in allBlueprints`,
        );
      }
      this.referencedBlueprintItems = [];
      return;
    }

    this.referencedBlueprint = blueprint;

    // Load as ref-* items for viewing only (not in main items array)
    const parentId = this.isBlueprintReferenceItem
      ? this.item.persistent_id
      : null;
    this.referencedBlueprintItems =
      this.blueprintItemDataEditorService.convertBlueprintItemsToQuoteItemData(
        blueprint.items,
        parentId,
        {
          persistentIdPrefix: `ref-${this.item.persistent_id}-`,
          useOriginalSiblingOrder: false,
          allBlueprints: this.allBlueprints,
        },
      );
  }

  /**
   * For blueprint reference rows: returns the blueprint's name from allBlueprints so the
   * tooltip can show it even when item.name has been set to a CAD object name.
   */
  get blueprintName(): string {
    if (!this.isBlueprintLinkItem) return '';

    const matchingBlueprint = this.allBlueprints.find(
      (blueprint) => String(blueprint.id) === String(this.item.blueprint_id),
    );

    return matchingBlueprint?.name ?? '';
  }

  /**
   * Checks if this item directly links to a blueprint (has a blueprint_id).
   * These items a real items within the blueprint that link to other blueprints, and aren't created on-demand.
   */
  get isBlueprintLinkItem(): boolean {
    return (
      this.item.blueprint_id !== null && this.item.blueprint_id !== undefined
    );
  }

  /**
   * Checks if this item is a ref-* item, which is a read-only representation of an item within a referenced blueprint.
   * These items are created on-demand when expanding a blueprint reference and have persistent_ids starting with "ref-".
   */
  get isBlueprintReferenceItem(): boolean {
    return this.item.persistent_id.startsWith('ref-');
  }

  /**
   * True when this row is a locally-created item (e.g. added via "Add Child" or "Add Item").
   * Used to show the default tax code in the selector when the item has no tax code set.
   */
  get isNewItem(): boolean {
    return this.item.source === 'created';
  }

  /**
   * Returns true if manual pricing mode is selected (no pricing model)
   */
  get isManualPricing(): boolean {
    const pricingModelId = this.form.get('pricing_model_id')?.value;
    return pricingModelId === null || pricingModelId === undefined;
  }

  /**
   * Returns true if the selected pricing model has output_quantity enabled
   * When output_quantity is enabled:
   * - Quantity is calculated by the pricing model (read-only)
   * - Unit cost is manually set by the user
   */
  get selectedPricingModelHasOutputQuantity(): boolean {
    const pricingModelId = this.form.get('pricing_model_id')?.value;
    if (pricingModelId === null || pricingModelId === undefined) {
      return false;
    }
    const model = this.pricingModelService.pricingModelsById()[pricingModelId];
    return model?.output_quantity ?? false;
  }

  get selectedPricingModelHasOutputUnitCost(): boolean {
    const pricingModelId = this.form.get('pricing_model_id')?.value;
    if (pricingModelId === null || pricingModelId === undefined) {
      return false;
    }
    const model = this.pricingModelService.pricingModelsById()[pricingModelId];
    return model?.output_unit_cost ?? false;
  }

  get capexTooltip(): string {
    const ids = this.item.capex_equipment_ids ?? [];
    if (ids.length === 0) {
      return 'Select CapEx equipment for this blueprint item.';
    }

    const names = this.equipmentOptions
      .filter((equipment) => ids.includes(equipment.id))
      .map((equipment) => equipment.name);
    return names.length > 0
      ? `Selected CapEx equipment: ${names.join(', ')}`
      : `${ids.length} CapEx equipment item${ids.length === 1 ? '' : 's'} selected`;
  }

  private areNumberArraysEqual(first: number[], second: number[]): boolean {
    if (first.length !== second.length) {
      return false;
    }

    const firstSorted = [...first].sort((a, b) => a - b);
    const secondSorted = [...second].sort((a, b) => a - b);
    return firstSorted.every((value, index) => value === secondSorted[index]);
  }

  onAddChildToThisItem(parentId: string): void {
    // Prevent adding children to referenced blueprint items
    if (parentId.startsWith('ref-')) {
      return;
    }
    this.addChild.emit(parentId);
  }

  onConsumablesChange(consumables: ConsumableSelection[]): void {
    this.blueprintItemDataEditorService.updateItem(this.item, { consumables });
  }

  onRemoveItem(): void {
    this.removeItem.emit(this.item.persistent_id);
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.quantityTypeSubscription) {
      this.quantityTypeSubscription.unsubscribe();
    }
  }

  protected readonly QuantityType = QuantityType;
}
